/**
 * EisenhowerMatrix Component - Eisenhower Matrix view for task prioritization
 * 
 * The Eisenhower Matrix (also kn// Droppable quadrant component
const DroppableQuadrant: React.FC<{
  id: string;
  title: string;
  subtitle: string;
  color: string;
  tasks: TodoItemType[];
  getListColor: (listId: number) => string | undefined;
  onToggleComplete: (task: TodoItemType) => void;
  draggedTaskId: number | null;
}> = ({ id, title, subtitle, color, tasks, getListColor, onToggleComplete, draggedTaskId }) => {ent-Important Matrix) divides tasks into four quadrants:
 * 1. Urgent & Important (Do First) - Red quadrant
 * 2. Not Urgent & Important (Schedule) - Green quadrant
 * 3. Urgent & Not Important (Delegate) - Yellow quadrant
 * 4. Not Urgent & Not Important (Eliminate) - Blue quadrant
 */
import React, { useMemo, useCallback, useState } from 'react';
import { Text, Paper, Checkbox, Stack, ActionIcon, Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { 
  DndContext, 
  DragOverlay,
  rectIntersection,
  pointerWithin,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  MeasuringStrategy
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Trash2, Check, X } from 'lucide-react';
import { useTodos, useTodoLists, useUpdateTodo, useDeleteTodo } from '../../hooks/useTodos';
import { reorderTodos } from '../../api/reorderApi';
import type { TodoItem as TodoItemType, UpdateTodoRequest } from '../../types/todo';
import { CustomDatePicker } from '../CustomDatePicker';
import { AssigneeDropdown } from '../AssigneeDropdown';
import { Modal } from '../Modal';
import { EditTodoForm, TaskTitleInput } from '../EditTodoForm';
import { useUser } from '../../contexts/UserContext';
import './EisenhowerMatrix.css';

interface EisenhowerMatrixProps {
  filters: {
    assignee: string;
    list: string | number;
    completed: 'today' | 'all' | 'hidden';
    tags: string[];
    search: string;
  };
}

// Draggable todo item component
const DraggableTodoItem: React.FC<{
  task: TodoItemType;
  stripeColor?: string;
  onToggleComplete: (task: TodoItemType) => void;
  quadrantType?: 'schedule' | 'delegate' | 'eliminate' | 'do-first';
  quadrantId?: string; // Add quadrant ID for drag operations
  onQuickAction?: (task: TodoItemType, action: string) => void;
  onEdit?: (task: TodoItemType) => void;
}> = ({ task, stripeColor, onToggleComplete, quadrantType, quadrantId, onQuickAction, onEdit }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Detect if device supports touch (mobile/tablet)
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    disabled: task.id < 0,
    data: {
      type: 'todo',
      task: task,
      quadrant: quadrantId || 'unclassified',
      quadrantType: quadrantType
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    cursor: 'grab',
    pointerEvents: isDragging ? ('none' as const) : ('auto' as const)
  };

  const getQuickActionButton = () => {
    if (!quadrantType || quadrantType === 'do-first') return null;

    // For schedule quadrant: show date picker if date exists OR on hover OR on mobile
    if (quadrantType === 'schedule' && (task.dueDate || isHovered || isTouchDevice)) {
      return (
        <div 
          onMouseDown={(e) => e.stopPropagation()} 
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            maxHeight: '24px',
            overflow: 'visible'
          }}
        >
          <CustomDatePicker
            value={task.dueDate || ''}
            onChange={(date) => {
              // Handle the date change
              onQuickAction?.(task, date);
            }}
            placeholder="Set date"
            displayFormat="inline"
            showIcon={true}
            showSnooze={true}
          />
        </div>
      );
    }

    // For delegate quadrant: show assignee picker if has assignee OR on hover OR on mobile
    if (quadrantType === 'delegate') {
      const hasAssignee = task.assignedTo && task.assignedTo !== null;
      const shouldShowPicker = hasAssignee || isHovered || isTouchDevice;
      
      if (shouldShowPicker) {
        return (
          <div 
            onMouseDown={(e) => e.stopPropagation()} 
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              maxHeight: '24px',
              overflow: 'visible'
            }}
          >
            <AssigneeDropdown
              value={task.assignedTo || undefined}
              onChange={(assignee) => {
                onQuickAction?.(task, assignee);
              }}
              placeholder="Assign"
              variant="inline"
              hideAnyOption={true}
            />
          </div>
        );
      }
    }

    // For eliminate quadrant: show delete icon on hover OR on mobile
    if (quadrantType === 'eliminate' && (isHovered || isTouchDevice)) {
      return (
        <Tooltip label="Delete task" position="top" withArrow>
          <ActionIcon
            size="xs"
            variant="subtle"
            color="red"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onQuickAction?.(task, 'eliminate');
            }}
          >
            <Trash2 size={12} />
          </ActionIcon>
        </Tooltip>
      );
    }

    return null;
  };  return (
    <Paper
      ref={setNodeRef}
      style={{
        ...style,
        padding: '6px 8px',
        minHeight: '32px',
        maxHeight: '32px',
        backgroundColor: 'var(--app-surface)',
        borderRadius: 'var(--mantine-radius-sm)',
        border: '1px solid var(--app-border)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...attributes}
      {...listeners}
    >
      {stripeColor && (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: stripeColor,
            flexShrink: 0,
          }}
        />
      )}
      <Checkbox
        checked={!!task.isCompleted}
        size="xs"
        radius="sm"
        style={{ flexShrink: 0 }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onToggleComplete(task);
        }}
      />
      <Text
        size="sm"
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textDecoration: task.isCompleted ? 'line-through' : undefined,
          opacity: task.isCompleted ? 0.6 : 1,
          color: task.priority === 'High' ? 'var(--app-danger-text)' : 'inherit',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onEdit?.(task);
        }}
      >
        {task.title}
      </Text>
      {getQuickActionButton()}
    </Paper>
  );
};

// Droppable quadrant component
const DroppableQuadrant: React.FC<{
  id: string;
  title: string;
  subtitle: string;
  className: string;
  tasks: TodoItemType[];
  getListColor: (listId: number) => string | undefined;
  onToggleComplete: (task: TodoItemType) => void;
  quadrantType: 'schedule' | 'delegate' | 'eliminate' | 'do-first';
  onQuickAction: (task: TodoItemType, action: string) => void;
  onEdit: (task: TodoItemType) => void;
}> = ({ id, title, subtitle, className, tasks, getListColor, onToggleComplete, quadrantType, onQuickAction, onEdit }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: id,
    data: { 
      type: 'quadrant',
      quadrant: id,
      classification: id 
    }
  });

  // Sort tasks by rank for proper display order
  const sortedTasks = [...tasks].sort((a, b) => a.rank - b.rank);
  const taskIds = sortedTasks.map(t => t.id);

  return (
    <div 
      ref={setNodeRef}
      className={`quadrant ${className}`}
      style={{
        backgroundColor: isOver ? 'rgba(255, 255, 255, 0.3)' : undefined,
        borderStyle: isOver ? 'dashed' : 'solid',
      }}
    >
      <div className="quadrant-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text className="quadrant-title" fw={700}>{title}</Text>
          <Text size="xs" c="dimmed" fw={600}>{tasks.length}</Text>
        </div>
        <Text className="quadrant-subtitle" size="xs">{subtitle}</Text>
      </div>
      <div className="quadrant-content">
        {tasks.length > 0 ? (
          <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
            <Stack gap={6}>
              {sortedTasks.map(task => (
                <DraggableTodoItem 
                  key={task.id} 
                  task={task}
                  quadrantId={id}
                  stripeColor={getListColor(task.listId)}
                  onToggleComplete={onToggleComplete}
                  quadrantType={quadrantType}
                  onQuickAction={onQuickAction}
                  onEdit={onEdit}
                />
              ))}
            </Stack>
          </SortableContext>
        ) : (
          <Text size="sm" c="dimmed" ta="center" mt="md">
            No tasks
          </Text>
        )}
      </div>
    </div>
  );
};

// Droppable unclassified box
const DroppableUnclassified: React.FC<{
  tasks: TodoItemType[];
  getListColor: (listId: number) => string | undefined;
  onToggleComplete: (task: TodoItemType) => void;
  onEdit: (task: TodoItemType) => void;
}> = ({ tasks, getListColor, onToggleComplete, onEdit }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'unclassified',
    data: { 
      type: 'quadrant',
      quadrant: 'unclassified',
      classification: 'unclassified' 
    }
  });

  // Sort tasks by rank for proper display order
  const sortedTasks = [...tasks].sort((a, b) => a.rank - b.rank);
  const taskIds = sortedTasks.map(t => t.id);

  return (
    <div 
      ref={setNodeRef}
      className="unclassified-box"
      style={{
        backgroundColor: isOver ? 'rgba(255, 255, 255, 0.5)' : undefined,
        borderStyle: isOver ? 'dashed' : 'solid',
      }}
    >
      <div className="unclassified-header">
        <Text className="unclassified-title" fw={700}>Unclassified</Text>
        <Text className="unclassified-subtitle" size="xs">
          {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
        </Text>
      </div>
      <div className="unclassified-content">
        {tasks.length > 0 ? (
          <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
            <Stack gap={6}>
              {sortedTasks.map(task => (
                <DraggableTodoItem 
                  key={task.id} 
                  task={task}
                  quadrantId="unclassified"
                  stripeColor={getListColor(task.listId)}
                  onToggleComplete={onToggleComplete}
                  onEdit={onEdit}
                />
              ))}
            </Stack>
          </SortableContext>
        ) : (
          <Text size="sm" c="dimmed" ta="center" mt="md">
            No incomplete tasks
          </Text>
        )}
      </div>
    </div>
  );
};

export const EisenhowerMatrix: React.FC<EisenhowerMatrixProps> = ({ filters }) => {
  const { data: todos = [], refetch: refetchTodos } = useTodos();
  const { data: todoLists = [] } = useTodoLists();
  const queryClient = useQueryClient();
  const updateTodoMutation = useUpdateTodo();
  const deleteTodoMutation = useDeleteTodo();
  const { currentUser } = useUser();
  const [draggedTask, setDraggedTask] = useState<TodoItemType | null>(null);
  const [editingTodo, setEditingTodo] = useState<TodoItemType | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateTodoRequest | null>(null);
  const [optimisticReordering, setOptimisticReordering] = useState<{
    sourceQuadrant: string;
    targetQuadrant: string;
    activeId: string;
    overId?: string;
    todos: {
      unclassified: TodoItemType[];
      urgentImportant: TodoItemType[];
      notUrgentImportant: TodoItemType[];
      urgentNotImportant: TodoItemType[];
      notUrgentNotImportant: TodoItemType[];
    };
  } | null>(null);

  // Configure sensors for both mouse and touch support (mobile + desktop)
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3, // Require 3px movement to start drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150, // Small delay to prevent accidental drags on mobile
        tolerance: 8, // Allow 8px movement during delay
      },
    })
  );

  const handleCloseEdit = () => {
    setEditingTodo(null);
    setEditFormData(null);
  };

  // Filter todos based on current filters and get only incomplete tasks
  const filteredTodos = useMemo(() => {
    return todos.filter(todo => {
      // Only show incomplete tasks
      if (todo.isCompleted) return false;

      // Apply list filter
      if (filters.list && filters.list !== 'all') {
        if (typeof filters.list === 'number' && todo.listId !== filters.list) {
          return false;
        }
      }
      
      // Apply assignee filter
      if (filters.assignee && filters.assignee !== 'any') {
        if (filters.assignee === 'unassigned') {
          if (todo.assignedTo && todo.assignedTo.trim() !== '') {
            return false;
          }
        } else {
          if (!todo.assignedTo || todo.assignedTo !== filters.assignee) {
            return false;
          }
        }
      }
      
      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (!todo.title.toLowerCase().includes(searchLower) && 
            !(todo.description || '').toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      
      // Apply tags filter
      if (filters.tags && filters.tags.length > 0) {
        let todoTags: string[] = [];
        if (Array.isArray(todo.tags)) {
          todoTags = todo.tags;
        } else if (typeof todo.tags === 'string' && todo.tags) {
          todoTags = (todo.tags as string).split(',').map((t: string) => t.trim().toLowerCase());
        }
        
        const hasMatchingTag = filters.tags.some(filterTag => 
          todoTags.some(todoTag => todoTag.toLowerCase() === filterTag.toLowerCase())
        );
        if (!hasMatchingTag) return false;
      }
      
      return true;
    });
  }, [todos, filters]);

  // Categorize tasks by Eisenhower classification
  const categorizedTasks = useMemo(() => {
    // If we have optimistic reordering, use that instead of the real data
    if (optimisticReordering) {
      return optimisticReordering.todos;
    }

    const unclassified: TodoItemType[] = [];
    const urgentImportant: TodoItemType[] = [];
    const notUrgentImportant: TodoItemType[] = [];
    const urgentNotImportant: TodoItemType[] = [];
    const notUrgentNotImportant: TodoItemType[] = [];

    filteredTodos.forEach(todo => {
      switch (todo.eisenhowerClassification) {
        case 'urgent-important':
          urgentImportant.push(todo);
          break;
        case 'not-urgent-important':
          notUrgentImportant.push(todo);
          break;
        case 'urgent-not-important':
          urgentNotImportant.push(todo);
          break;
        case 'not-urgent-not-important':
          notUrgentNotImportant.push(todo);
          break;
        default:
          unclassified.push(todo);
      }
    });

    // Sort by rank within each quadrant
    unclassified.sort((a, b) => a.rank - b.rank);
    urgentImportant.sort((a, b) => a.rank - b.rank);
    notUrgentImportant.sort((a, b) => a.rank - b.rank);
    urgentNotImportant.sort((a, b) => a.rank - b.rank);
    notUrgentNotImportant.sort((a, b) => a.rank - b.rank);

    return {
      unclassified,
      urgentImportant,
      notUrgentImportant,
      urgentNotImportant,
      notUrgentNotImportant
    };
  }, [filteredTodos, optimisticReordering]);

  // Handle toggle complete
  const handleToggleComplete = useCallback((task: TodoItemType) => {
    // Optimistic update
    queryClient.setQueriesData({ queryKey: ['todos'] }, (old: any) => {
      if (!old) return old;
      const apply = (x: any) => {
        if (x.id !== task.id) return x;
        return { ...x, isCompleted: !task.isCompleted };
      };
      if (Array.isArray(old)) return old.map(apply);
      if (Array.isArray(old?.data)) return { ...old, data: old.data.map(apply) };
      if (Array.isArray(old?.pages)) {
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: Array.isArray(page?.data) ? page.data.map(apply) : page.data,
          })),
        };
      }
      return old;
    });

    updateTodoMutation.mutate({ id: task.id, data: { isCompleted: !task.isCompleted } }, {
      onError: () => queryClient.invalidateQueries({ queryKey: ['todos'] })
    });
  }, [queryClient, updateTodoMutation]);

  // Helper function to convert quadrant ID to categorizedTasks key
  const quadrantIdToKey = (quadrantId: string): keyof typeof categorizedTasks => {
    switch (quadrantId) {
      case 'urgent-important':
        return 'urgentImportant';
      case 'not-urgent-important':
        return 'notUrgentImportant';
      case 'urgent-not-important':
        return 'urgentNotImportant';
      case 'not-urgent-not-important':
        return 'notUrgentNotImportant';
      case 'unclassified':
      default:
        return 'unclassified';
    }
  };

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const task = event.active.data.current?.task as TodoItemType | undefined;
    if (task) {
      setDraggedTask(task);
    }
    
    // Clear any previous optimistic reordering
    setOptimisticReordering(null);
  };

  // Handle drag over - used for visual feedback during drag
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || !active.data.current) {
      setOptimisticReordering(null);
      return;
    }

    const activeTask = active.data.current.task as TodoItemType;
    const activeQuadrant = active.data.current.quadrant as string;
    const overData = over.data.current;

    if (!activeTask || !activeQuadrant) {
      setOptimisticReordering(null);
      return;
    }

    // Determine target quadrant and insertion index
    let targetQuadrant = activeQuadrant;
    let insertIndex = 0;

    if (overData?.type === 'quadrant') {
      // Dropping on empty quadrant area
      targetQuadrant = overData.quadrant;
      insertIndex = 0;
    } else if (overData?.type === 'todo') {
      // Dropping on another todo
      targetQuadrant = overData.quadrant;
      
      // Safety check: ensure we have a valid target quadrant
      if (!targetQuadrant) {
        setOptimisticReordering(null);
        return;
      }
      
      const targetQuadrantKey = quadrantIdToKey(targetQuadrant);
      const targetTodos = categorizedTasks[targetQuadrantKey];
      
      // Safety check: if targetTodos is undefined, default to empty array
      if (!targetTodos) {
        insertIndex = 0;
      } else {
        const overTaskId = overData.task?.id;
        const overIndex = targetTodos.findIndex(t => t.id === overTaskId);
        
        // Replace the target item (replacement semantics)
        if (overIndex !== -1) {
          insertIndex = overIndex; // Replace target
        } else {
          insertIndex = targetTodos.length; // Insert at end if target not found
        }
      }
    }

    // Only create optimistic updates for cross-quadrant moves (NOT for reordering within same quadrant)
    if (activeQuadrant !== targetQuadrant) {
      
      // Use clean base state from real todos
      const baseCategorizedTasks = {
        unclassified: [] as TodoItemType[],
        urgentImportant: [] as TodoItemType[],
        notUrgentImportant: [] as TodoItemType[],
        urgentNotImportant: [] as TodoItemType[],
        notUrgentNotImportant: [] as TodoItemType[]
      };

      // Populate with real todos only
      filteredTodos.forEach(todo => {
        if (todo.id > 0) { // Only real todos
          switch (todo.eisenhowerClassification) {
            case 'urgent-important':
              baseCategorizedTasks.urgentImportant.push(todo);
              break;
            case 'not-urgent-important':
              baseCategorizedTasks.notUrgentImportant.push(todo);
              break;
            case 'urgent-not-important':
              baseCategorizedTasks.urgentNotImportant.push(todo);
              break;
            case 'not-urgent-not-important':
              baseCategorizedTasks.notUrgentNotImportant.push(todo);
              break;
            default:
              baseCategorizedTasks.unclassified.push(todo);
          }
        }
      });

      // Sort by rank within each quadrant
      Object.keys(baseCategorizedTasks).forEach(key => {
        baseCategorizedTasks[key as keyof typeof baseCategorizedTasks].sort((a, b) => a.rank - b.rank);
      });

      // Create working copy
      const newCategorizedTasks = {
        unclassified: [...baseCategorizedTasks.unclassified],
        urgentImportant: [...baseCategorizedTasks.urgentImportant],
        notUrgentImportant: [...baseCategorizedTasks.notUrgentImportant],
        urgentNotImportant: [...baseCategorizedTasks.urgentNotImportant],
        notUrgentNotImportant: [...baseCategorizedTasks.notUrgentNotImportant]
      };

      // Remove from source quadrant using the mapping function
      const sourceQuadrantKey = quadrantIdToKey(activeQuadrant);
      if (newCategorizedTasks[sourceQuadrantKey]) {
        newCategorizedTasks[sourceQuadrantKey] = newCategorizedTasks[sourceQuadrantKey].filter(t => t.id !== activeTask.id);
      }

      // Determine the new classification for cross-quadrant moves
      let newClassification = activeTask.eisenhowerClassification;
      if (activeQuadrant !== targetQuadrant) {
        // Use the target quadrant ID directly (it's already in kebab-case format)
        newClassification = targetQuadrant === 'unclassified' ? null : targetQuadrant as typeof activeTask.eisenhowerClassification;
      }

      // Create task with new classification
      const taskWithNewClassification = {
        ...activeTask,
        eisenhowerClassification: newClassification
      };

      // Insert into target quadrant at calculated position using the mapping function
      const targetQuadrantKey = quadrantIdToKey(targetQuadrant);
      if (newCategorizedTasks[targetQuadrantKey]) {
        newCategorizedTasks[targetQuadrantKey].splice(insertIndex, 0, taskWithNewClassification);
      }

      // Update optimistic state
      setOptimisticReordering({
        sourceQuadrant: activeQuadrant,
        targetQuadrant: targetQuadrant,
        activeId: String(active.id),
        overId: String(over.id),
        todos: newCategorizedTasks
      });
    }
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedTask(null);
    
    if (!over || !active.data.current || !currentUser?.email) {
      setOptimisticReordering(null);
      return;
    }

    const overId = String(over.id);

    try {
      const activeTask = active.data.current.task as TodoItemType;
      const overData = over.data.current;

      if (!activeTask) {
        setOptimisticReordering(null);
        return;
      }
      
      // Find the original task from filtered todos to ensure we have fresh data
      const originalTask = filteredTodos.find(t => t.id === activeTask.id);
      if (!originalTask) {
        console.warn('Original task not found in filteredTodos:', activeTask.id);
        setOptimisticReordering(null);
        return;
      }

      // CRITICAL: Determine the actual source quadrant from the REAL task data
      // (not from drag data which might be stale from optimistic updates)
      const actualClassification = originalTask.eisenhowerClassification;
      let activeQuadrant = 'unclassified';
      if (actualClassification === 'urgent-important') {
        activeQuadrant = 'urgent-important';
      } else if (actualClassification === 'not-urgent-important') {
        activeQuadrant = 'not-urgent-important';
      } else if (actualClassification === 'urgent-not-important') {
        activeQuadrant = 'urgent-not-important';
      } else if (actualClassification === 'not-urgent-not-important') {
        activeQuadrant = 'not-urgent-not-important';
      }

      // Determine target quadrant from drop location
      let targetQuadrant = activeQuadrant;
      
      if (overData?.type === 'quadrant') {
        targetQuadrant = overData.quadrant;
      } else if (overData?.type === 'todo') {
        targetQuadrant = overData.quadrant;
      }

      const activeQuadrantKey = quadrantIdToKey(activeQuadrant);
      const targetQuadrantKey = quadrantIdToKey(targetQuadrant);

      // Decision point: same-quadrant vs cross-quadrant based on REAL source
      if (activeQuadrant === targetQuadrant) {
        // Same-quadrant reordering - use categorizedTasks (no optimistic state for same-quadrant)
        const currentQuadrantTodos = categorizedTasks[activeQuadrantKey] || [];
        const oldIndex = currentQuadrantTodos.findIndex(t => t.id === originalTask.id);
        
        if (overId.startsWith('todo-') || typeof over.id === 'number') {
          const overTaskId = typeof over.id === 'number' ? over.id : parseInt(overId.replace('todo-', ''), 10);
          const newIndex = currentQuadrantTodos.findIndex(t => t.id === overTaskId);
          
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            // Use arrayMove to reorder within quadrant
            const reorderedTodos = arrayMove(currentQuadrantTodos, oldIndex, newIndex);
            
            // Update categorizedTasks with reordered todos for this quadrant
            const updatedCategorizedTasks = { ...categorizedTasks };
            updatedCategorizedTasks[activeQuadrantKey] = reorderedTodos;
            
            // Create final order for backend
            const allTodosInNewOrder: TodoItemType[] = [];
            const quadrantOrder = ['urgentImportant', 'notUrgentImportant', 'urgentNotImportant', 'notUrgentNotImportant', 'unclassified'] as const;
            
            quadrantOrder.forEach(key => {
              const quadrantTodos = updatedCategorizedTasks[key] || [];
              allTodosInNewOrder.push(...quadrantTodos);
            });
            
            // Send reorder to backend
            const allOrderedIds = allTodosInNewOrder.map(t => t.id).filter(id => id > 0);
            const listId = filters.list === 'all' ? undefined : (typeof filters.list === 'number' ? filters.list : undefined);
            
            if (allOrderedIds.length > 0) {
              await reorderTodos(allOrderedIds, currentUser.email, listId);
              // Refetch todos to reflect new order (reorder API doesn't update TanStack cache)
              await refetchTodos();
            }
            
            // Clear optimistic state after same-quadrant reorder completes
            setOptimisticReordering(null);
          }
        } else {
          // No reordering needed, just clear optimistic state
          setOptimisticReordering(null);
        }
      } else {
        // Cross-quadrant move - rebuild from base data to avoid optimistic state issues
        const baseCategorizedTasks = {
          unclassified: [] as TodoItemType[],
          urgentImportant: [] as TodoItemType[],
          notUrgentImportant: [] as TodoItemType[],
          urgentNotImportant: [] as TodoItemType[],
          notUrgentNotImportant: [] as TodoItemType[]
        };

        // Populate with real todos only
        filteredTodos.forEach(todo => {
          if (todo.id > 0) {
            switch (todo.eisenhowerClassification) {
              case 'urgent-important':
                baseCategorizedTasks.urgentImportant.push(todo);
                break;
              case 'not-urgent-important':
                baseCategorizedTasks.notUrgentImportant.push(todo);
                break;
              case 'urgent-not-important':
                baseCategorizedTasks.urgentNotImportant.push(todo);
                break;
              case 'not-urgent-not-important':
                baseCategorizedTasks.notUrgentNotImportant.push(todo);
                break;
              default:
                baseCategorizedTasks.unclassified.push(todo);
            }
          }
        });

        // Sort by rank within each quadrant
        Object.keys(baseCategorizedTasks).forEach(key => {
          baseCategorizedTasks[key as keyof typeof baseCategorizedTasks].sort((a, b) => a.rank - b.rank);
        });

        // Handle cross-quadrant move
        const sourceQuadrantTodos = [...(baseCategorizedTasks[activeQuadrantKey] || [])];
        const targetQuadrantTodos = [...(baseCategorizedTasks[targetQuadrantKey] || [])];
        
        const draggedIndex = sourceQuadrantTodos.findIndex(t => t.id === originalTask.id);
        
        if (draggedIndex !== -1) {
          // Remove from source
          const [draggedTodo] = sourceQuadrantTodos.splice(draggedIndex, 1);
          
          // Determine new classification
          let newClassification: typeof originalTask.eisenhowerClassification = null;
          if (targetQuadrant !== 'unclassified') {
            newClassification = targetQuadrant as typeof originalTask.eisenhowerClassification;
          }
          
          // Update classification for target quadrant
          const updatedTodo = {
            ...draggedTodo,
            eisenhowerClassification: newClassification
          };
          
          // Determine target position
          let targetIndex = targetQuadrantTodos.length; // Default to end
          
          if ((overId.startsWith('todo-') || typeof over.id === 'number') && overData?.type === 'todo') {
            // Dropping on another todo - insert at that position
            const overTodoId = typeof over.id === 'number' ? over.id : parseInt(overId.replace('todo-', ''), 10);
            const overIndex = targetQuadrantTodos.findIndex(t => t.id === overTodoId);
            if (overIndex !== -1) {
              targetIndex = overIndex; // Insert at target's position
            }
          }
          
          targetQuadrantTodos.splice(targetIndex, 0, updatedTodo);
          
          // Update all quadrants with the new arrangement
          const updatedCategorizedTasks = { ...baseCategorizedTasks };
          updatedCategorizedTasks[activeQuadrantKey] = sourceQuadrantTodos;
          updatedCategorizedTasks[targetQuadrantKey] = targetQuadrantTodos;
          
          // Create final order for backend
          const allTodosInNewOrder: TodoItemType[] = [];
          const quadrantOrder = ['urgentImportant', 'notUrgentImportant', 'urgentNotImportant', 'notUrgentNotImportant', 'unclassified'] as const;
          
          quadrantOrder.forEach(key => {
            const quadrantTodos = updatedCategorizedTasks[key] || [];
            allTodosInNewOrder.push(...quadrantTodos);
          });
          
          // Send reorder to backend
          const allOrderedIds = allTodosInNewOrder.map(t => t.id).filter(id => id > 0);
          const listId = filters.list === 'all' ? undefined : (typeof filters.list === 'number' ? filters.list : undefined);
          
          if (allOrderedIds.length > 0) {
            await reorderTodos(allOrderedIds, currentUser.email, listId);
            // Refetch todos to reflect new order (reorder API doesn't update TanStack cache)
            await refetchTodos();
          }
          
          // Clear optimistic state after cross-quadrant operations complete
          setOptimisticReordering(null);
          
          // Update the classification via mutation
          const updateData: UpdateTodoRequest = {
            eisenhowerClassification: newClassification
          };
          
          await new Promise<void>((resolve, reject) => {
            updateTodoMutation.mutate({
              id: originalTask.id,
              data: updateData
            }, {
              onSuccess: () => resolve(),
              onError: (error) => reject(error)
            });
          });
          
          // Clear optimistic state after mutation completes (safety)
          setOptimisticReordering(null);
        }
      }
    } catch (error) {
      console.error('Failed to move task:', error);
      // Clear optimistic state immediately on error
      setOptimisticReordering(null);
      queryClient.invalidateQueries({ queryKey: ['todos'] });
    }
  };

  // Handle quick actions from quadrants
  const handleQuickAction = useCallback((task: TodoItemType, action: string) => {
    if (action === 'eliminate') {
      // Delete action
      if (confirm(`Are you sure you want to delete "${task.title}"?`)) {
        deleteTodoMutation.mutate(task.id);
      }
      return;
    }
    
    // Detect if this is an assignee action or date action
    // Assignee actions: '__unassigned__' or emails (contain @) or empty string from assignee clear
    const isAssigneeAction = action === '__unassigned__' || action.includes('@');
    
    if (isAssigneeAction) {
      // Assignee action
      const assignedTo = action === '__unassigned__' ? '' : action;
      updateTodoMutation.mutate({
        id: task.id,
        data: { assignedTo }
      });
    } else {
      // Date action (date string, empty string, or null)
      // Empty string from date picker clear should be null
      updateTodoMutation.mutate({
        id: task.id,
        data: { dueDate: action || null }
      });
    }
  }, [deleteTodoMutation, updateTodoMutation]);

  // Get list color for stripe (when showing all lists)
  const getListColor = (listId: number) => {
    if (filters.list === 'all') {
      return todoLists.find(l => l.id === listId)?.color;
    }
    return undefined;
  };
  
  return (
    <>
    <DndContext
      sensors={sensors}
      collisionDetection={(args) => {
        const byPointer = pointerWithin(args);
        if (byPointer.length > 0) return byPointer;
        return rectIntersection(args);
      }}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      autoScroll={{
        enabled: true,
        threshold: {
          x: 0.15,
          y: 0.15,
        },
        acceleration: 15,
        interval: 5,
        // Enable scrolling for both window and parent containers
        layoutShiftCompensation: false,
      }}
    >
      <div className="eisenhower-matrix-container">
        {/* Matrix Grid */}
        <div className="matrix-grid">
          {/* Unclassified items box (left side) */}
          <DroppableUnclassified
            tasks={categorizedTasks.unclassified}
            getListColor={getListColor}
            onToggleComplete={handleToggleComplete}
            onEdit={setEditingTodo}
          />

        {/* Main matrix content */}
        <div className="matrix-content">
          {/* Quadrants grid with both horizontal and vertical labels */}
          <div className="quadrants-wrapper">
            {/* Horizontal axis label (top) */}
            <div className="horizontal-axis">
              <div className="axis-label horizontal-label">
                <span className="axis-text">URGENT</span>
              </div>
              <div className="axis-arrow horizontal-arrow">→</div>
              <div className="axis-label horizontal-label right">
                <span className="axis-text">NOT URGENT</span>
              </div>
            </div>

            {/* Vertical axis label (left side of quadrants) */}
            <div className="vertical-axis">
              <div className="axis-label vertical-label">
                <span className="axis-text">IMPORTANT</span>
              </div>
              <div className="axis-label vertical-label bottom">
                <span className="axis-text">NOT IMPORTANT</span>
              </div>
            </div>

            {/* Quadrants grid */}
            <div className="quadrants-grid">
            {/* Quadrant 1: Urgent & Important (Do First) */}
            <DroppableQuadrant
              id="urgent-important"
              title="Do First"
              subtitle="Urgent & Important"
              className="quadrant-urgent-important"
              tasks={categorizedTasks.urgentImportant}
              getListColor={getListColor}
              onToggleComplete={handleToggleComplete}
              quadrantType="do-first"
              onQuickAction={handleQuickAction}
              onEdit={setEditingTodo}
            />

            {/* Quadrant 2: Not Urgent & Important (Schedule) */}
            <DroppableQuadrant
              id="not-urgent-important"
              title="Schedule"
              subtitle="Not Urgent & Important"
              className="quadrant-not-urgent-important"
              tasks={categorizedTasks.notUrgentImportant}
              getListColor={getListColor}
              onToggleComplete={handleToggleComplete}
              quadrantType="schedule"
              onQuickAction={handleQuickAction}
              onEdit={setEditingTodo}
            />

            {/* Quadrant 3: Urgent & Not Important (Delegate) */}
            <DroppableQuadrant
              id="urgent-not-important"
              title="Delegate"
              subtitle="Urgent & Not Important"
              className="quadrant-urgent-not-important"
              tasks={categorizedTasks.urgentNotImportant}
              getListColor={getListColor}
              onToggleComplete={handleToggleComplete}
              quadrantType="delegate"
              onQuickAction={handleQuickAction}
              onEdit={setEditingTodo}
            />

            {/* Quadrant 4: Not Urgent & Not Important (Eliminate) */}
            <DroppableQuadrant
              id="not-urgent-not-important"
              title="Eliminate"
              subtitle="Not Urgent & Not Important"
              className="quadrant-not-urgent-not-important"
              tasks={categorizedTasks.notUrgentNotImportant}
              getListColor={getListColor}
              onToggleComplete={handleToggleComplete}
              quadrantType="eliminate"
              onQuickAction={handleQuickAction}
              onEdit={setEditingTodo}
            />
          </div>
          </div>
        </div>
      </div>
    </div>

      <DragOverlay>
        {draggedTask && (
          <Paper
            style={{ 
              transform: 'rotate(1deg)',
              opacity: 0.95,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
              pointerEvents: 'none',
              userSelect: 'none',
              padding: '6px 8px',
              minHeight: '32px',
              maxHeight: '32px',
              backgroundColor: 'var(--app-surface)',
              borderRadius: 'var(--mantine-radius-sm)',
              border: '1px solid var(--app-border)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              overflow: 'hidden',
              cursor: 'grabbing',
            }}
          >
            {draggedTask.listId && (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: filters.list === 'all' ? todoLists.find(l => l.id === draggedTask.listId)?.color : undefined,
                  flexShrink: 0,
                }}
              />
            )}
            <Checkbox
              checked={!!draggedTask.isCompleted}
              size="xs"
              radius="sm"
              style={{ flexShrink: 0 }}
              disabled
            />
            <Text
              size="sm"
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: draggedTask.isCompleted ? 'line-through' : undefined,
                opacity: draggedTask.isCompleted ? 0.6 : 1,
                color: draggedTask.priority === 'High' ? 'var(--app-danger-text)' : 'inherit',
              }}
            >
              {draggedTask.title}
            </Text>
          </Paper>
        )}
      </DragOverlay>
    </DndContext>

    {/* Edit Task Modal */}
    {editingTodo && (
      <Modal
        isOpen={!!editingTodo}
        onClose={handleCloseEdit}
        hideCloseButton={true}
        headerContent={
          <TaskTitleInput
            value={editingTodo.title}
            onChange={(value) => {
              // Update both editingTodo and editFormData
              setEditingTodo(prev => prev ? { ...prev, title: value } : null);
              setEditFormData(prev => prev ? { ...prev, title: value } : { ...editingTodo, title: value });
            }}
            autoFocus={true}
            isCompleted={editingTodo.isCompleted}
            onToggleComplete={() => {
              const newCompletedState = !editingTodo.isCompleted;
              // Update local state immediately for UI feedback
              setEditingTodo(prev => prev ? { ...prev, isCompleted: newCompletedState } : null);
              // Then update in the backend
              handleToggleComplete(editingTodo);
              // Close modal after a longer delay with visual feedback if marking as complete
              if (newCompletedState) {
                setTimeout(() => {
                  handleCloseEdit();
                }, 600);
              }
            }}
          />
        }
        actionButtons={
          <>
            <button
              type="button"
              onClick={() => {
                if (editFormData && editingTodo && editingTodo.id > 0) {
                  updateTodoMutation.mutate({
                    id: editingTodo.id,
                    data: editFormData
                  }, {
                    onSuccess: () => {
                      handleCloseEdit();
                    },
                  });
                }
              }}
              disabled={!editFormData || !editFormData.title?.trim() || updateTodoMutation.isPending || (editingTodo && editingTodo.id < 0)}
              className="header-btn header-btn-save"
              title={editingTodo && editingTodo.id < 0 ? "Please wait for task to be created..." : "Save task"}
              aria-label="Save task"
            >
              <Check size={16} />
            </button>
            <button
              type="button"
              onClick={() => {
                if (editingTodo) {
                  deleteTodoMutation.mutate(editingTodo.id, {
                    onSuccess: () => {
                      handleCloseEdit();
                    }
                  });
                }
              }}
              disabled={deleteTodoMutation.isPending}
              className="header-btn header-btn-delete"
              title="Delete task"
              aria-label="Delete task"
            >
              <Trash2 size={16} />
            </button>
            <button
              type="button"
              onClick={handleCloseEdit}
              className="header-btn header-btn-close"
              title="Cancel"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </>
        }
      >
        <EditTodoForm
          todo={editingTodo}
          onComplete={handleCloseEdit}
          onDataChange={setEditFormData}
          renderTitleInline={false}
        />
      </Modal>
    )}
    </>
  );
};
